import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';

import { useAdminGetDeliveryCityById } from '../../hooks/admin-get-delivery-city-by-id.hook';
import { useUpdateDeliveryCity } from '../../hooks/update-delivery-city.hook';
import { useArchiveDeliveryCity } from '../../hooks/archive-delivery-city.hook';
import { useRestoreDeliveryCity } from '../../hooks/restore-delivery-city.hook';
import { DeliveryCity } from '../../types/delivery-city.type';
import { UpdateDeliveryCityRequestDTO } from '../../types/dtos/update-delivery-city.dto';
import { ADMIN_DELIVERY_CITIES_APP_PATHS } from '../../constants/delivery-cities-app-paths.constants';
import { DELIVERY_CITY_CONFIRM_DIALOG_CONTENT } from '../../constants/delivery-city-details.constants';
import { DeliveryCityInformationsTab } from '../../components/Containers/DeliveryCityInformationsTab.component';
import { ConfirmActionDialog } from '../../../shared/components/Modals/ConfirmActionDialog.component';
import { NotFoundState } from '../../../shared/components/States/NotFoundState.component';

type DialogState = { kind: 'none' } | { kind: 'confirm'; action: 'archive' | 'restore' };

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '1rem',
  },
});

export default function AdminDeliveryCityDetailsView() {
  const classes = useStyles();
  const { deliveryCityId } = useParams<{ deliveryCityId: string }>();

  const { isLoading, deliveryCity, refetch } = useAdminGetDeliveryCityById({ deliveryCityId });
  const { updateDeliveryCity, isSubmitting: isUpdating } = useUpdateDeliveryCity();
  const { archiveDeliveryCity, isSubmitting: isArchiving } = useArchiveDeliveryCity();
  const { restoreDeliveryCity, isSubmitting: isRestoring } = useRestoreDeliveryCity();

  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });

  const closeDialog = () => setDialog({ kind: 'none' });

  const handleDeliveryCityUpdated = async (data: UpdateDeliveryCityRequestDTO): Promise<DeliveryCity | null> => {
    if (isLoading || !deliveryCity) return null;

    const updatedDeliveryCity = await updateDeliveryCity({ deliveryCityId: deliveryCity.id, deliveryCityData: data });
    if (!updatedDeliveryCity) return null;

    await refetch();
    return updatedDeliveryCity;
  };

  const handleConfirm = async () => {
    if (dialog.kind !== 'confirm' || !deliveryCity) return;

    const result =
      dialog.action === 'archive'
        ? await archiveDeliveryCity(deliveryCity.id)
        : await restoreDeliveryCity(deliveryCity.id);
    if (!result) return;

    closeDialog();
    await refetch();
  };

  return (
    <section className={classes.root}>
      {isLoading && <Skeleton variant="rect" animation="wave" height={92} />}
      {!isLoading && !deliveryCity && (
        <NotFoundState
          title="Ville de livraison introuvable"
          backLabel="Retour aux villes"
          backTo={ADMIN_DELIVERY_CITIES_APP_PATHS.DELIVERY_CITIES_LIST}
        />
      )}
      {!isLoading && deliveryCity && (
        <DeliveryCityInformationsTab
          deliveryCity={deliveryCity}
          isUpdating={isUpdating}
          updateDeliveryCity={handleDeliveryCityUpdated}
          onArchiveActionClick={() =>
            setDialog({ kind: 'confirm', action: deliveryCity.archivedAt !== null ? 'restore' : 'archive' })
          }
        />
      )}
      {dialog.kind === 'confirm' && deliveryCity && (
        <ConfirmActionDialog
          open
          title={DELIVERY_CITY_CONFIRM_DIALOG_CONTENT[dialog.action].title}
          description={DELIVERY_CITY_CONFIRM_DIALOG_CONTENT[dialog.action].description(deliveryCity.name)}
          confirmLabel={DELIVERY_CITY_CONFIRM_DIALOG_CONTENT[dialog.action].confirmLabel}
          isSubmitting={isArchiving || isRestoring}
          onConfirm={handleConfirm}
          onClose={closeDialog}
        />
      )}
    </section>
  );
}
